<script setup lang="ts">
  import { onMounted, ref, computed } from 'vue';
  import { ShippingType } from '@othercommerce/domain';
  import { useFormContext, setModelWithContext, getModelFromContext, useTranslator } from '@othercommerce/foundation';

  interface Props {
    service: string,
    types: ShippingType[],
    mapKey: string | null,
    isSandbox: boolean,
  }

  const props = defineProps<Props>();

  const form = useFormContext();
  const loading = ref(true);
  const translator = useTranslator();
  const map = ref();

  const shippingPointId = computed(() => {
    return getModelFromContext('shippingPointId', form, null);
  });

  const shippingPointName = computed(() => {
    return getModelFromContext('shippingPointName', form, null);
  });

  // Map point types. When both parcel lockers and service points are we
  // return undefined to force widger show both of them. Otherwise, we return
  // only a single type.
  const type = computed(() => {
    let mapped = [];

    if (props.types.includes(ShippingType.PARCEL_LOCKER)) {
      mapped.push('parcel_machine');
    }

    if (props.types.includes(ShippingType.SERVICE_POINT)) {
      mapped.push('service_point');
    }

    if (mapped.length > 1) {
      return undefined;
    }

    return mapped[0];
  });

  const error = computed(() => {
    let pointId = form!.errors.value.shippingPointId || [];
    let pointName = form!.errors.value.shippingPointName || [];

    if (pointId.length > 0) {
      return pointId[0];
    } else if (pointName.length > 0) {
      return pointName[0];
    }

    return undefined;
  });

  function openMap(mapKey: string | null) {
    if (mapKey === null) {
      return alert(translator.trans('SHOP.CART.MESSAGE.SHIPPING_ALERT_UNABLE_TO_USE_PICKUP_POINT'));
    }

    map.value = new window.Furgonetka.Map({
      apiKey: mapKey,
      courierServices: [props.service],
      type: type.value,
      env: props.isSandbox ? 'sandbox' : 'production',
      callback: (params: any) => {
        setModelWithContext('shippingPointId', form, params.point.code);
        setModelWithContext('shippingPointName', form, params.point.name);
      },
    });

    map.value.show();
  }

  onMounted(() => {
    if (!window.Furgonetka) {
      const script = document.createElement('script');

      script.src = 'https://furgonetka.pl/js/dist/map/map.js';
      script.async = true;
      script.onload = () => {
        loading.value = false;
      };

      document.head.appendChild(script);
    } else {
      loading.value = false;
    }
  });
</script>
<template>
  <div class="furgonetka-map-widget">
    <SfDataLoader :loading="loading">
      <div class="furgonetka-map-widget__point">
        <div class="furgonetka-map-widget__icon">
          <SfIconPointer aria-hidden="true" />
        </div>
        <div class="furgonetka-map-widget__details">
          <div v-if="shippingPointId && shippingPointName" class="furgonetka-map-widget__name">
            {{ shippingPointId }} {{ shippingPointName }}
          </div>
          <SfButton small @click="openMap(mapKey)" transparent type="button" :loading>
            {{ shippingPointName ? $t('SHOP.CART.ACTION.SHIPPING_CHANGE_PICKUP_POINT') : $t('SHOP.CART.ACTION.SHIPPING_SELECT_PICKUP_POINT') }}
          </SfButton>
        </div>
      </div>
    </SfDataLoader>
    <p v-if="error" class="control__error" v-html="error" />
  </div>
</template>
<style lang="scss">
  .furgonetka-map-widget {
    &__point {
      display: flex;
      align-items: center;
      gap: 1rem;
      border: 1px dashed var(--secondary-border);
      padding: 1rem;
      border-radius: var(--border-radius);
    }

    &__icon {
      font-size: 1.5rem;
      line-height: 1;
    }

    &__details {
      display: flex;
      align-items: center;
      line-height: 1.5rem;
      gap: 0.5rem;
    }

    &__name {
      font-weight: var(--font-weight-bold);
    }

    .control__error {
      color: var(--sf-input-error-color);
      font-size: var(--sf-input-error-font-size);
      padding: var(--sf-input-error-padding-y) var(--sf-input-error-padding-x);
      scroll-margin-top: var(--sf-input-error-scroll-margin-top);
      scroll-margin-bottom: var(--sf-input-error-scroll-margin-bottom);
      margin-top: 1rem;
    }
  }
</style>
